import { useEffect, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useUser } from "@/features/auth";
import { PageLoadingSkeleton } from "./LoadingSkeletons";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isInitialized, isLoading, checkAuth } = useAuth();
  const user = useUser();

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (isInitialized && !isAuthenticated && !isLoading) {
      const redirectPath = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?redirect=${redirectPath}`, { replace: true });
    }
  }, [isInitialized, isAuthenticated, isLoading, navigate, location]);

  // Redirect non-admins away from admin routes
  useEffect(() => {
    if (isInitialized && isAuthenticated && requireAdmin && user?.type !== "admin") {
      navigate("/portal", { replace: true });
    }
  }, [isInitialized, isAuthenticated, requireAdmin, user, navigate]);

  // Show loading state while checking auth
  if (!isInitialized || isLoading) {
    return <PageLoadingSkeleton />;
  }

  // Don't render children if not authenticated (redirect will happen)
  if (!isAuthenticated) {
    return <PageLoadingSkeleton />;
  }

  // Don't render if admin required but user is not admin
  if (requireAdmin && user?.type !== "admin") {
    return <PageLoadingSkeleton />;
  }

  return <>{children}</>;
}
